import { Formik } from "formik";
import * as yup from "yup";
import { Input } from "../Base/Input";
import { TextArea } from "../Base/TextArea";
import { Button } from "../Base/Button";
import type { User } from "@repo/types";
import { CheckboxInput } from "../Base/CheckboxInput";
import { DialogBody, DialogFooter } from "../Base/Dialog";

export interface ContactFormSubmissionData {
  email: string;
  subject: string;
  message: string;
  browser: boolean;
}

export interface ContactFormProps {
  handleCancel: () => void;
  handleComplete: (data: ContactFormSubmissionData) => void | Promise<void>;
  user?: User;
  withinDialog?: boolean;
}

export const ContactForm = ({
  handleCancel,
  handleComplete,
  user,
  withinDialog,
}: ContactFormProps) => {
  const validationSchema = yup.object({
    subject: yup.string().required("Subject is required"),
    message: yup.string().required("Message is required"),
  });

  return (
    <div className="w-full">
      <Formik
        initialValues={
          {
            email: user?.email ?? "",
            subject: "",
            message: "",
            browser: true,
          } satisfies ContactFormSubmissionData as ContactFormSubmissionData
        }
        validationSchema={validationSchema}
        onSubmit={async (values) => {
          await handleComplete(values);
        }}
      >
        {({
          values,
          handleSubmit,
          handleBlur,
          handleChange,
          setFieldValue,
          isValid,
          isSubmitting,
        }) => {
          const footer = (
            <>
              {" "}
              <Button
                type="button"
                disabled={isSubmitting}
                variant="destructive"
                onClick={() => handleCancel()}
              >
                Cancel
              </Button>
              <div className="flex-1" />
              <Button type="submit" disabled={isSubmitting || !isValid}>
                Send
              </Button>
            </>
          );

          const form = (
            <div className="flex flex-col gap-y-4">
              <Input
                id="email"
                name="email"
                label="Your Email"
                value={values.email}
                disabled
              />
              <Input
                id="subject"
                name="subject"
                label="Subject"
                onBlur={handleBlur}
                value={values.subject}
                onChange={handleChange}
              />
              <TextArea
                id="message"
                name="message"
                label="Message"
                onBlur={handleBlur}
                value={values.message}
                onChange={handleChange}
              />
              <CheckboxInput
                id="browser"
                name="browser"
                label="Include browser information (This may be important in diagnosing your issue)"
                containerStyle="flex-row items-center gap-x-2"
                checked={values.browser}
                onCheckedChange={(value) => setFieldValue("browser", value)}
              />
              {!withinDialog && (
                <div className="flex flex-row justify-between">{footer}</div>
              )}
            </div>
          );

          return (
            <form onSubmit={handleSubmit}>
              {withinDialog ? (
                <>
                  <DialogBody>{form}</DialogBody>
                  <DialogFooter className="justify-between">
                    {footer}
                  </DialogFooter>
                </>
              ) : (
                form
              )}
            </form>
          );
        }}
      </Formik>
    </div>
  );
};
